import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const STORAGE_KEY = 'cc_cookie_consent_v1';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Defer a tick so server-rendered hydration is consistent
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Small delay so the banner doesn't flash on initial page paint
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    }
  }, []);

  const persist = (choice) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, ts: new Date().toISOString() })
    );
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 sm:bottom-6 sm:right-6 sm:left-auto sm:max-w-md z-50 bg-white rounded-2xl shadow-2xl border border-border p-5"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-full bg-primary-light flex-shrink-0">
          <Cookie className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-text-primary mb-1">{t('cookie.title', 'We use cookies')}</h3>
          <p className="text-sm text-text-secondary leading-relaxed">
            {t('cookie.body', 'We use essential cookies to keep you signed in and remember your preferences. Read our')}{' '}
            <Link to="/privacy-policy" className="text-primary underline font-medium">
              {t('cookie.privacy', 'Privacy Policy')}
            </Link>{' '}
            {t('cookie.bodySuffix', 'to learn more.')}
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={() => persist('accepted')}
              className="bg-text-primary hover:bg-primary text-white font-semibold text-sm px-5 py-2 rounded-full transition-colors"
              data-testid="cookie-accept-button"
            >
              {t('cookie.acceptAll', 'Accept all')}
            </button>
            <button
              onClick={() => persist('essential_only')}
              className="border border-border hover:bg-surface-muted text-text-primary font-semibold text-sm px-5 py-2 rounded-full transition-colors"
              data-testid="cookie-essential-button"
            >
              {t('cookie.essentialOnly', 'Essential only')}
            </button>
          </div>
        </div>
        <button
          onClick={() => persist('dismissed')}
          aria-label={t('cookie.dismiss', 'Dismiss cookie banner')}
          className="p-1 rounded-full hover:bg-surface-muted text-text-secondary flex-shrink-0"
          data-testid="cookie-dismiss-button"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
